import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, TextField, CircularProgress } from '@mui/material';
import { useStripe, useElements, PaymentElement } from '@stripe/react-stripe-js';
import { sendRequest } from '../../../api/shootAPI';
import API from '../../../api/endpoints';
import { UserContext } from '../../../contexts/User';

function CheckoutForm({
  price, membership, onPaymentSuccess, onCouponApplied, subscriptionMode = 'override',
}) {
  const stripe = useStripe();
  const elements = useElements();
  const { activeWorkspace, setActiveWorkspace } = useContext(UserContext);
  const [errorMessage, setErrorMessage] = useState();
  const [loading, setLoading] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [transactionId, setTransactionId] = useState(null);

  // Coupon state
  const [couponCode, setCouponCode] = useState('');
  const [couponValidating, setCouponValidating] = useState(false);
  const [couponError, setCouponError] = useState('');
  const [validCoupon, setValidCoupon] = useState(null);
  const [discountInfo, setDiscountInfo] = useState(null);

  // Check if transaction is free (below $0.50)
  const MINIMUM_CHARGE_AMOUNT = 0.50;
  const finalPrice = discountInfo ? discountInfo.finalPrice : price;
  const isFreeTransaction = finalPrice < MINIMUM_CHARGE_AMOUNT;

  const handleError = (error) => {
    setLoading(false);
    setErrorMessage(error.message);
  };

  const calculateDiscount = (coupon, amount) => {
    if (coupon.discountType === 'percentage') {
      return amount * (coupon.discountValue / 100);
    }
    if (coupon.discountType === 'fixed') {
      return Math.min(coupon.discountValue, amount);
    }
    return 0;
  };

  const handleValidateCoupon = async () => {
    if (!couponCode.trim()) {
      setCouponError('Please enter a coupon code');
      return;
    }

    setCouponValidating(true);
    setCouponError('');

    try {
      const res = await sendRequest({
        method: 'post',
        url: `workspace/${activeWorkspace.id}/coupons/validate`,
        data: { code: couponCode },
      });

      if (res.data?.valid) {
        setValidCoupon(res.data.coupon);
        setCouponError('');
        // Notify parent about discount
        const discount = calculateDiscount(res.data.coupon, price);
        const finalPriceCalc = price - discount;
        const discountData = {
          coupon: res.data.coupon,
          discount,
          finalPrice: finalPriceCalc,
        };
        setDiscountInfo(discountData);
        if (onCouponApplied) {
          onCouponApplied(discountData);
        }
      } else {
        setValidCoupon(null);
        setCouponError(res.data?.error || 'Invalid coupon code');
      }
    } catch (err) {
      console.error('Failed to validate coupon:', err);
      setCouponError(err.response?.data?.error || 'Invalid coupon code');
      setValidCoupon(null);
      if (onCouponApplied) {
        onCouponApplied(null);
      }
    } finally {
      setCouponValidating(false);
    }
  };

  const handleRemoveCoupon = () => {
    setCouponCode('');
    setValidCoupon(null);
    setCouponError('');
    setDiscountInfo(null);
    if (onCouponApplied) {
      onCouponApplied(null);
    }
  };

  const handleSubmit = async (event) => {
    // We don't want to let default form submission happen here,
    // which would refresh the page.
    event.preventDefault();

    if (!stripe) {
      // Stripe.js hasn't yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return;
    }

    setLoading(true);

    // For free transactions, skip Stripe payment element validation
    if (!isFreeTransaction) {
      // Trigger form validation and wallet collection
      const { error: submitError } = await elements.submit();
      if (submitError) {
        handleError(submitError);
        return;
      }
    }

    const data = {
      price,
      membership,
      subscriptionMode,
      ...(validCoupon ? { couponCode: validCoupon.code } : {}),
    };

    // Create the PaymentIntent/SetupIntent and obtain clientSecret
    const res = await sendRequest({
      method: 'post',
      url: API.WORKSPACE.subscription(activeWorkspace.id),
      data,
    });

    const {
      client_secret: clientSecret,
      transactionId: txnId,
      paymentMethod,
      // eslint-disable-next-line no-unused-vars
      isFreeTransaction: apiIsFreeTransaction,
      intentType,
    } = res.data || {};

    if (!clientSecret) {
      handleError({ message: 'Failed to initialize payment. Please try again.' });
      return;
    }

    // Store transaction ID for status update (using state for potential future use)
    if (txnId) {
      setTransactionId(txnId);
      // Store in sessionStorage for redirect case
      if (!onPaymentSuccess) {
        sessionStorage.setItem('pendingTransactionId', txnId);
      }
    }

    // Update membership before confirming payment
    const updateData = {
      membership,
      paymentMethod,
    };

    const response = await sendRequest({
      method: 'put',
      url: API.WORKSPACE.updateMembership(activeWorkspace.id),
      data: updateData,
    });

    if (response.success) {
      setActiveWorkspace({
        ...activeWorkspace,
        membership,
      });
    }

    // Handle free transactions (SetupIntent) differently from paid transactions (PaymentIntent)
    if (isFreeTransaction && intentType === 'setup') {
      // For free transactions, no Stripe confirmation needed
      // Backend already created SetupIntent and marked transaction as success
      // Just proceed with success flow
      setLoading(false);
      if (onPaymentSuccess) {
        onPaymentSuccess();
      }
      // If no callback, user stays on page (transaction already successful)
    } else {
      // Confirm the PaymentIntent using the details collected by the Payment Element
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        clientSecret,
        confirmParams: {
          return_url: window.location.href,
        },
        redirect: onPaymentSuccess ? 'if_required' : 'always',
      });

      if (error) {
        // This point is only reached if there's an immediate error when
        // confirming the payment. Show the error to your customer (for example, payment details incomplete)
        // Update transaction status to failed
        if (txnId) {
          try {
            await sendRequest({
              method: 'put',
              url: API.WORKSPACE.updateTransactionStatus(activeWorkspace.id, txnId),
              data: { status: 'failed', paymentIntentId: paymentIntent?.id },
            });
          } catch (updateErr) {
            console.error('Failed to update transaction status:', updateErr);
          }
        }
        handleError(error);
      } else {
        // Payment successful - update transaction status
        if (txnId && paymentIntent?.id) {
          try {
            await sendRequest({
              method: 'put',
              url: API.WORKSPACE.updateTransactionStatus(activeWorkspace.id, txnId),
              data: { status: 'success', paymentIntentId: paymentIntent.id },
            });
          } catch (updateErr) {
            console.error('Failed to update transaction status:', updateErr);
          }
        }

        // eslint-disable-next-line no-lonely-if
        if (onPaymentSuccess) {
          // If callback provided (signup flow), call it instead of redirecting
          onPaymentSuccess();
        }
        // Otherwise, your customer is redirected to your `return_url`.
      }
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      {/* Coupon Code Section */}
      <div className="mb-3">
        {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
        <label
          htmlFor="couponCode"
          className="form-label fw-medium size-xs mb-2"
        >
          Have a Coupon Code? (Optional)
        </label>
        {!validCoupon ? (
          <div className="d-flex gap-2">
            <TextField
              id="couponCode"
              placeholder="Enter coupon code"
              value={couponCode}
              onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
              disabled={couponValidating}
              size="small"
              sx={{ flex: 1 }}
              error={!!couponError}
              helperText={couponError}
            />
            <Button
              onClick={handleValidateCoupon}
              disabled={!couponCode.trim() || couponValidating}
              sx={{
                textTransform: 'none',
                minWidth: '100px',
              }}
              variant="outlined"
            >
              {couponValidating ? <CircularProgress size={20} /> : 'Apply'}
            </Button>
          </div>
        ) : (
          <div
            className="p-3"
            style={{ backgroundColor: '#E8F5E9', borderRadius: '4px', border: '1px solid #4CAF50' }}
          >
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <div className="fw-semibold" style={{ color: '#2E7D32' }}>
                  ✓
                  {' '}
                  {validCoupon.code}
                  {' '}
                  Applied!
                </div>
                <div className="size-xs" style={{ color: '#66BB6A' }}>
                  {validCoupon.description}
                </div>
              </div>
              <Button
                onClick={handleRemoveCoupon}
                size="small"
                sx={{ textTransform: 'none' }}
              >
                Remove
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Payment Element or Free Transaction Message */}
      {isFreeTransaction ? (
        <div
          className="mb-3 p-3"
          style={{ backgroundColor: '#E3F2FD', borderRadius: '4px', border: '1px solid #2196F3' }}
        >
          <div className="fw-semibold mb-2" style={{ color: '#1976D2' }}>
            🎉 Free Transaction
          </div>
          <div className="size-sm" style={{ color: '#1565C0' }}>
            No payment required! Your payment method will be saved for future billing without any charge today.
          </div>
        </div>
      ) : (
        <PaymentElement className="pb-3" />
      )}

      <Button
        sx={{
          font: 'Inter',
          fontSize: '16px',
          lineHeight: '24px',
          fontWeight: '500',
          textTransform: 'none',
          borderRadius: '4px',
          padding: '7px 29px',
          color: '#FFFFFF',
          border: '1px solid',
          backgroundColor: (!stripe || loading) ? '#BDBDBD' : '#53A551',
          '&:hover': {
            backgroundColor: '#53A551',
          },
        }}
        color="primary"
        type="submit"
        disabled={loading || (!isFreeTransaction && !stripe)}
      >
        {isFreeTransaction ? 'Activate Free Trial' : 'Submit'}
      </Button>
      {errorMessage && <div className="pt-2 size-xs">{errorMessage}</div>}
    </form>
  );
}

CheckoutForm.propTypes = {
  price: PropTypes.number.isRequired,
  membership: PropTypes.number.isRequired,
  onPaymentSuccess: PropTypes.func,
  onCouponApplied: PropTypes.func,
  subscriptionMode: PropTypes.string,
};

CheckoutForm.defaultProps = {
  onPaymentSuccess: null,
  onCouponApplied: null,
  subscriptionMode: 'override',
};

export default CheckoutForm;
